
import { DATE_FORMAT, formatShortISO, parse } from '@eneco/common/date';
import { getProductTypeCombinations } from '@eneco/dc/services';
import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_Products_Offers_OfferProductsPublicRequest,
  ResponseModels_Products_ProductCombinationResponseModel,
} from '@monorepo-types/dc';
import { fromPromise } from 'xstate';

import { FlowConsumerContextEnergy as FlowContext } from '../../../context';

type EnergyChoiceContext = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  values: FlowContext;
  allowDynamicOrHybridOnly: boolean;
};

const hasUnsupportedProducts = (data?: ResponseModels_Products_ProductCombinationResponseModel['data']): boolean =>
  !!data?.commodity?.some(productCombination => !!productCombination?.includes('warmthProducts'));

export const getEnergyChoiceTypes = async ({
  businessUnit,
  label,
  values,
  allowDynamicOrHybridOnly,
}: EnergyChoiceContext) => {
  const requestBody: RequestModels_Products_Offers_OfferProductsPublicRequest = {
    data: {
      postalCode: values.postalCode?.replace(/\s/g, ''),
      houseNumber: values.houseNumber ?? 0,
      houseNumberSuffix: values.houseNumberSuffix,
      gasValue: 0,
      electricityRedeliveryHigh: 0,
      electricityRedeliveryLow: 0,
      electricityRedeliveryTotal: 0,
      isMkb: !!values.isSME,
      offerType: 'NewEnergyContract',
      voucherCode: values.voucherCode,
      contractStartDate: values.deliveryDate && formatShortISO(parse(values.deliveryDate, DATE_FORMAT)),
      urgent: !!values?.isUrgent,
      ci: values?.ci ?? !!values?.isMoving,
      movingCustomer: !!values?.isMoving,
      includeRentalProducts: false,
      allowDynamicOrHybridOnly,
    },
  };

  try {
    const response = await getProductTypeCombinations({
      businessUnit: businessUnit,
      label: label,
      requestBody,
    });

    if (!response?.data?.commodity?.length) {
      throw new Error('No products found');
    }

    if (hasUnsupportedProducts(response?.data)) {
      throw new Error('No supported products');
    }

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch energy types');
  }
};

export type getEnergyChoiceTypesOutput = Awaited<ReturnType<typeof getEnergyChoiceTypes>>;

export const actors = {
  getEnergyTypes: fromPromise<getEnergyChoiceTypesOutput, EnergyChoiceContext>(
    async ({ input }) => await getEnergyChoiceTypes(input),
  ),
};
